import { useEffect, useState } from 'preact/hooks'
import type { Dispatch, StateUpdater } from 'preact/hooks'
import { useFieldArray } from 'react-hook-form'
import type { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form'
import type { WebViewDto, OnlineEventSpeakerViewDto } from '@/api/webs'
import { useLookups } from '@/hooks/useLookups'
import type { OptionDto } from '@/types/lookups'
import { sanitizeDateTimeInput } from '@/utils/dateTimeFormatters'
import { useAIForm, handleListFieldUpdate } from '@/hooks/useAIForm'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { useAgendaEditor } from '@/hooks/useAgendaEditor'
import { fieldLimits } from '@/types/field-limits.generated'

export type SpeakerUi = OnlineEventSpeakerViewDto
export interface SpeakerUiWithPic extends SpeakerUi { ProfilePicUrl?: string | null }

export type OnlineEventAIForm = {
  overview: string
  agenda: { value: string }[]
  eventType: string
  modeType: string
  title: string
  timeZone: string
  startDateTime: string
  endDateTime: string
  conferenceId: string
  channelId: string
  holdCrmSyncUntilEnd: boolean
}

const resolveOptionKey = (val: string | undefined, options: OptionDto[]): string => {
  const v = (val || '').trim()
  if (!v) return ''
  const byKey = options.find(o => o.Key === v)
  if (byKey) return byKey.Key
  const byDesc = options.find(o => o.Description === v)
  if (byDesc) return byDesc.Key
  return v
}

function buildDefaultValues(
  evt: WebViewDto['OnlineEvent'],
  eventTypeOptions: OptionDto[],
  modeTypeOptions: OptionDto[],
): OnlineEventAIForm {
  return {
    overview: evt?.Overview || '',
    agenda: (evt?.Agenda ?? []).map(v => ({ value: v })),
    eventType: resolveOptionKey(evt?.EventType, eventTypeOptions),
    modeType: resolveOptionKey(evt?.ModeType, modeTypeOptions),
    title: evt?.Title || '',
    timeZone: evt?.TimeZone || '',
    startDateTime: sanitizeDateTimeInput(evt?.StartDateTime),
    endDateTime: sanitizeDateTimeInput(evt?.EndDateTime),
    conferenceId: evt?.ConferenceId || '',
    channelId: evt?.ChannelId || '',
    holdCrmSyncUntilEnd: evt?.HoldCrmSyncUntilEnd ?? false,
  }
}

interface UseOnlineEventFormParams {
  web: WebViewDto
  onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
  speakers: SpeakerUiWithPic[]
  showAddParticipant: boolean
  showEditModal: boolean
}

type AgendaArray = UseFieldArrayReturn<OnlineEventAIForm, 'agenda'>
type OnlineEventForm = UseFormReturn<OnlineEventAIForm>

/**
 * Stated rather than inferred, because `build-clean` emits a declaration for this hook and the
 * inferred shape reaches react-hook-form types that TS can only name by pointing at
 * node_modules/react-hook-form/dist/types — a path it refuses to write into a .d.ts (TS2742).
 * AgendaSection used to supply that anchor incidentally, by instantiating those types against a
 * concrete `any`; once its `control` became generic, the naming had to move here.
 *
 * It is also the honest thing to do for a hook that OnlineEventPanel destructures twenty-odd
 * members out of.
 */
export interface UseOnlineEventFormReturn {
  editingDetails: boolean
  setEditingDetails: Dispatch<StateUpdater<boolean>>
  savingDetails: boolean
  setSavingDetails: Dispatch<StateUpdater<boolean>>
  editingAgenda: boolean
  setEditingAgenda: Dispatch<StateUpdater<boolean>>
  editingOverview: boolean
  setEditingOverview: Dispatch<StateUpdater<boolean>>
  savingOverview: boolean
  setSavingOverview: Dispatch<StateUpdater<boolean>>
  /** True while any one of the three sections is open for editing — the others lock on it. */
  anySectionEditing: boolean

  register: OnlineEventForm['register']
  watch: OnlineEventForm['watch']
  setValue: OnlineEventForm['setValue']
  getFieldState: OnlineEventForm['getFieldState']
  formState: OnlineEventForm['formState']
  control: OnlineEventForm['control']

  agendaFields: AgendaArray['fields']
  append: AgendaArray['append']
  remove: AgendaArray['remove']
  move: AgendaArray['move']
  /** Live value of the agenda field array, as watched off the form. */
  watched: OnlineEventAIForm['agenda'] | undefined

  saving: boolean
  hasDirtyEmpty: boolean
  normalizedInitial: string[]
  agendaChanged: boolean
  saveAgenda: () => Promise<void>
  cancelAgendaEdit: () => void
}

export function useOnlineEventForm({ web, onPatchWeb, speakers, showAddParticipant, showEditModal }: UseOnlineEventFormParams): UseOnlineEventFormReturn {
  const onlineEvent = web.OnlineEvent

  const [editingDetails, setEditingDetails] = useState(false)
  const [savingDetails, setSavingDetails] = useState(false)
  const [editingAgenda, setEditingAgenda] = useState(false)
  const [editingOverview, setEditingOverview] = useState(false)
  const [savingOverview, setSavingOverview] = useState(false)

  const anySectionEditing = editingOverview || editingDetails || editingAgenda
  const modalIsOpen = showAddParticipant || showEditModal

  const { getOnlineEventTypes, getOnlineModeTypes } = useLookups()
  const onlineEventTypeOptions: OptionDto[] = getOnlineEventTypes() || []
  const onlineModeTypeOptions: OptionDto[] = getOnlineModeTypes() || []

  const { form } = useAIForm<OnlineEventAIForm>({
    mode: 'onChange',
    defaultValues: buildDefaultValues(onlineEvent, onlineEventTypeOptions, onlineModeTypeOptions),
  }, {
    aiContext: {
      campaignId: web.CampaignId,
      webId: web.Id,
      location: ChatLocations.WebOnlineEventEditor,
      entityType: ChatEntityTypes.Web,
      entityId: web.Id,
      viewMode: anySectionEditing ? ChatViewModes.Edit : ChatViewModes.View,
    },
    fields: [
      { aiName: 'onlineEventOverview', formPath: 'overview', displayName: 'Event Overview', fieldType: 'textarea', maxLength: 5000, isEditable: editingOverview },
      { aiName: 'onlineEventTitle', formPath: 'title', displayName: 'Event Title', fieldType: 'text', maxLength: 200, isEditable: editingDetails },
      // maxLength is the PER-ITEM cap the API validates (WebValidationConstants
      // .OnlineEventAgendaItemMaxLength), not a cap on the list. Sending it means Webster is told
      // the limit in its own context instead of having to reach for the field-limits tool to find
      // it — and when it doesn't reach, writing items the save then rejects with a 422 (ORB-475).
      { aiName: 'onlineEventAgenda', formPath: 'agenda', displayName: 'Agenda Items', fieldType: 'textarea', maxLength: fieldLimits.OnlineEvent.OnlineEventAgendaItem.MaxLength, isEditable: editingAgenda, isListField: true },
    ],
    loadedData: { speakers: speakers.map(s => ({ name: s.Name, title: s.Title, company: s.CompanyName })) },
    contextSuppressed: modalIsOpen,
    onFieldUpdate: (aiName, value) => {
      if (aiName === 'onlineEventAgenda') handleListFieldUpdate(value, append)
    },
  })
  const { register, watch, reset, setValue, getFieldState, formState, control } = form
  const { fields: agendaFields, append, remove, move } = useFieldArray({ control, name: 'agenda' })

  useEffect(() => {
    reset(buildDefaultValues(onlineEvent, onlineEventTypeOptions, onlineModeTypeOptions))
  }, [onlineEvent])

  const watched = watch('agenda')
  const {
    saving, hasDirtyEmpty, normalizedInitial, agendaChanged,
    saveAgenda, cancelAgendaEdit,
  } = useAgendaEditor({ web, onPatchWeb, form, watched, getFieldState, formState, setEditingAgenda })

  return {
    editingDetails, setEditingDetails,
    savingDetails, setSavingDetails,
    editingAgenda, setEditingAgenda,
    editingOverview, setEditingOverview,
    savingOverview, setSavingOverview,
    anySectionEditing,
    register, watch, setValue, getFieldState, formState, control,
    agendaFields, append, remove, move,
    watched,
    saving, hasDirtyEmpty, normalizedInitial, agendaChanged,
    saveAgenda, cancelAgendaEdit,
  }
}
